// Touch navigation for bpmn-js canvases.
//
// diagram-js pans only on mouse drags and zooms only on the wheel, so on
// phones and tablets a BPMN diagram can't be moved and a pinch zooms the whole
// page instead. This adds one-finger pan and two-finger pinch zoom to the
// diagram container through the public Canvas API (scroll / zoom — the same
// calls diagram-js's own wheel zoom makes). Taps are left alone, so bpmn-js
// still receives them as clicks for selecting and identifying elements.
//
// Pair it with `touch-action: none` on the container so the browser doesn't
// scroll or zoom the page under the gesture.

// A one-finger touch has to travel this far before it pans, so taps stay taps.
const TOUCH_SLOP_PX = 8;

// Touches that start on diagram chrome keep their own behaviour.
const IGNORE_SELECTOR = [
  ".djs-palette",
  ".djs-context-pad",
  ".djs-popup",
  ".djs-direct-editing-parent",
  "button",
  "input",
  "select",
  "textarea",
  "a",
  "[contenteditable='true']",
].join(", ");

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * @param {HTMLElement} container element bpmn-js was mounted into
 * @param {object} canvas the viewer's `canvas` service
 * @param {{ minZoom?: number, maxZoom?: number }} [options]
 * @returns {() => void} detaches the listeners
 */
export function attachBpmnTouchNavigation(container, canvas, { minZoom = 0.15, maxZoom = 4 } = {}) {
  if (!container || !canvas || typeof container.addEventListener !== "function") {
    return () => {};
  }

  let gesture = null;
  let frame = 0;
  // Work collected since the last frame: a pan delta and/or a zoom target.
  let panDx = 0;
  let panDy = 0;
  let zoomTo = null;

  const local = (touch) => {
    const rect = container.getBoundingClientRect();
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const flush = () => {
    frame = 0;
    try {
      if (panDx || panDy) canvas.scroll({ dx: panDx, dy: panDy });
      if (zoomTo) canvas.zoom(zoomTo.scale, zoomTo.center);
    } catch (e) {
      // A transient zero-size viewbox (mid re-import) can make the matrix
      // math throw; the next move simply tries again.
    }
    panDx = 0;
    panDy = 0;
    zoomTo = null;
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(flush);
  };

  const begin = (touches) => {
    if (touches.length >= 2) {
      const a = local(touches[0]);
      const b = local(touches[1]);
      let startScale = 1;
      try {
        startScale = canvas.zoom() || 1;
      } catch (e) {
        startScale = 1;
      }
      gesture = {
        kind: "pinch",
        startScale,
        startDist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
        lastMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
    } else {
      const p = local(touches[0]);
      gesture = {
        kind: "pan",
        origin: p,
        last: p,
        // Continuing after a pinch: already a drag, no slop to cross.
        moved: Boolean(gesture),
      };
    }
  };

  const onTouchStart = (event) => {
    if (event.target && event.target.closest && event.target.closest(IGNORE_SELECTOR)) {
      gesture = null;
      return;
    }
    begin(event.touches);
  };

  const onTouchMove = (event) => {
    if (!gesture) return;
    if (gesture.kind === "pinch") {
      if (event.touches.length < 2) return;
      const a = local(event.touches[0]);
      const b = local(event.touches[1]);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      // Move with the fingers, then zoom about where they now are.
      panDx += mid.x - gesture.lastMid.x;
      panDy += mid.y - gesture.lastMid.y;
      gesture.lastMid = mid;
      zoomTo = {
        scale: clamp(gesture.startScale * (dist / gesture.startDist), minZoom, maxZoom),
        center: mid,
      };
    } else {
      const p = local(event.touches[0]);
      if (!gesture.moved) {
        if (Math.hypot(p.x - gesture.origin.x, p.y - gesture.origin.y) < TOUCH_SLOP_PX) return;
        gesture.moved = true;
      }
      panDx += p.x - gesture.last.x;
      panDy += p.y - gesture.last.y;
      gesture.last = p;
    }
    if (event.cancelable) event.preventDefault();
    schedule();
  };

  const onTouchEnd = (event) => {
    if (!gesture) return;
    if (event.touches.length > 0) {
      begin(event.touches);
      return;
    }
    gesture = null;
    if (frame) {
      window.cancelAnimationFrame(frame);
      flush();
    }
  };

  container.addEventListener("touchstart", onTouchStart, { passive: true });
  container.addEventListener("touchmove", onTouchMove, { passive: false });
  container.addEventListener("touchend", onTouchEnd);
  container.addEventListener("touchcancel", onTouchEnd);

  return () => {
    container.removeEventListener("touchstart", onTouchStart);
    container.removeEventListener("touchmove", onTouchMove);
    container.removeEventListener("touchend", onTouchEnd);
    container.removeEventListener("touchcancel", onTouchEnd);
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
  };
}
